
import React, { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { TariffCardsGrid } from "./TariffCardsGrid";
import { TariffSuccess } from "./TariffSuccess";
import { TariffActionButton } from "./TariffActionButton";
import { tariffs } from "./tariff.constants";
import { useTariffSelection } from "./useTariffSelection";
import { generateDealNumber } from "@/utils/dateUtils";

interface FullScreenTariffSelectionProps {
  onBack: () => void;
  amoCustomerCode?: string | null;
  amoDealCode?: string | null;
  clientNumber?: string | null;
}

export const FullScreenTariffSelection: React.FC<FullScreenTariffSelectionProps> = ({
  onBack,
  amoCustomerCode,
  amoDealCode,
  clientNumber,
}) => {
  const [sentTariff, setSentTariff] = useState<null | { title: string; price: number }>(null);
  const [showCheck, setShowCheck] = useState(false);
  const [dealNumber, setDealNumber] = useState<string | null>(null);
  const isMobile = useIsMobile();
  
  // Добавляем логирование для отладки
  useEffect(() => {
    console.log("FullScreenTariffSelection смонтирован, параметры:", {
      amoCustomerCode,
      amoDealCode,
      clientNumber,
    });
  }, [amoCustomerCode, amoDealCode, clientNumber]);
  
  const { selected, setSelected, sendTariff, loading } = useTariffSelection({
    amoCustomerCode, 
    amoDealCode, 
    clientNumber,
    onSuccess: (tariff) => {
      setSentTariff(tariff);
      setDealNumber(generateDealNumber());
      setShowCheck(true);
      // Автоматическое закрытие экрана выбора после успешного выбора тарифа
      setTimeout(() => {
        setShowCheck(false);
        setSentTariff(null); // очистка для повторного использования
        setSelected(null);
        setDealNumber(null);
        onBack();
      }, 2000);
    },
  });

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-gray-900 via-slate-900 to-slate-950 flex flex-col items-center justify-start w-full h-full overflow-auto p-0">
      {/* Кнопка "Назад" */}
      <div className="w-full max-w-3xl mx-auto flex items-center mt-4 px-2 sm:mt-6">
        <button
          className="flex items-center text-base text-white font-semibold bg-slate-700 rounded-xl px-3 py-1.5 mb-2 hover:bg-slate-600 transition shadow-sm"
          onClick={onBack}
        >
          <ArrowLeft className="mr-1 w-4 h-4" />
          Назад
        </button>
      </div>
      <div className="w-full max-w-3xl mx-auto flex-1 flex flex-col justify-start items-center px-2 pb-4 sm:pb-6">
        <div className="w-full">
          <div className="text-white text-xl sm:text-2xl font-bold text-center mt-1 mb-4 sm:mb-6 select-none">Выберите тариф аренды</div>
          {!sentTariff ? (
            <div className="flex flex-col">
              <div className="overflow-auto pb-24 sm:pb-24" style={{ maxHeight: isMobile ? "calc(100vh - 180px)" : "auto" }}>
                <TariffCardsGrid
                  selected={selected}
                  setSelected={setSelected}
                  isMobile={isMobile}
                />
              </div>
              <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900 pt-6 pb-4 px-4 sm:px-0 sm:pt-6 sm:pb-6">
                <TariffActionButton
                  disabled={loading || !selected}
                  loading={loading}
                  onClick={() => {
                    if (selected) {
                      console.log("Отправляем тариф:", selected);
                      const selectedTariff = tariffs.find((t) => t.key === selected)!;
                      sendTariff(selectedTariff);
                    }
                  }}
                />
                <div className="mt-2 text-center flex justify-center items-center gap-2 text-white text-xs select-none">
                  При выборе тарифа учитывайте ваши рабочие задачи
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col justify-center items-center w-full">
              <TariffSuccess
                showCheck={showCheck}
                sentTariff={sentTariff}
                clientNumber={clientNumber}
                dealNumber={dealNumber || undefined}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
